import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { NameCheckResponse } from '../interfaces/company.interface';
import { VerifyIdTinResponse } from '../interfaces/company.interface';
import { AmendmentTypeEnum } from '../enums/amendment.enum';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
export interface PublicCompanySearchItem {
  id?: string;
  companyId?: string;
  companyName?: string;
  tinNumber?: string;
  companyType?: string;
  companyCategory?: string;
  companyStatus?: string;
  [key: string]: any;
}

export interface AppointmentResponse {
  status: boolean;
  message: string;
  data: Appointment[];
}

export interface Appointment {
  id: string;
  start: string;
  end: string;
  applicantId: string;
  myBooking: boolean;
  status: 'BOOKED' | 'APPROVED' | 'REJECTED';
  license: string;
  comment?: string;
  companyName?: string | null;
}

export interface KycVerificationDetails {
  applicationId: string;
  applicationNumber: string;
  status: string;
  allApproved: boolean;
  unapprovedEmails: string[];
  nonExistentEmails: string[];
  rejectedEmails: string[];
}

export interface KycVerificationResponse {
  status: boolean;
  message: string;
  data: KycVerificationDetails;
}

export interface KycResendPendingEmailsDetails {
  applicationsProcessed: number;
  emailsSent: number;
  emailsNotified: string[];
}

export interface KycResendPendingEmailsResponse {
  status: boolean;
  message: string;
  data: KycResendPendingEmailsDetails;
}

export interface KycMismatchPartyInformation {
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  emailAddress?: string | null;
  identificationDocumentType?: string | null;
  identificationDocType?: string | null;
  documentType?: string | null;
  identificationDocumentNumber?: string | null;
  identificationDocNumber?: string | null;
  identificationNumber?: string | null;
  nationalId?: string | null;
  [key: string]: unknown;
}

export interface KycMismatch {
  id: string;
  mismatchId?: string;
  applicationId?: string;
  shareholderId?: string;
  status?: string;
  registeredInformation?: KycMismatchPartyInformation | null;
  kycReturnedInformation?: KycMismatchPartyInformation | null;
  [key: string]: unknown;
}

export interface KycMismatchResponse {
  status?: boolean;
  message?: string;
  data: KycMismatch;
}

export interface KycMismatchListResponse {
  status?: boolean;
  message?: string;
  data: KycMismatch[] | { content?: KycMismatch[] };
}

export interface KycMismatchDecisionPayload {
  comment?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  currentUser: any;
  userId: any;
  constructor(
    private http: HttpClient,
  ) { }
  
  private baseUrl = environment.baseUrl;
  private publicUrl = environment.baseUrl + 'public/';
  // get user from local storage
  getLocalUser() {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      this.currentUser = null;
      this.userId = null;
      return;
    }
    try {
      const user = JSON.parse(userStr);
      this.currentUser = user || null;
      this.userId = user?.userId || null;
    } catch {
      this.userId = null;
      this.currentUser = null;
    }
  }

  // applications
  submitApplication(payload: any) {
    if (
      payload?.companyAmendmentDto?.companyAmendmentType === AmendmentTypeEnum.COMPANY_OWNERSHIP_AMENDMENT ||
      payload?.companyAmendmentDto?.companyAmendmentType === AmendmentTypeEnum.COMPANY_TRANSFER_SHARE_AMENDMENT) {
      const url = `${this.baseUrl}applications/amendment-ownership-control`;
      return this.http.post<any>(url, payload, {});
    }
    else {
      const url = `${this.baseUrl}applications`;
      return this.http.post<any>(url, payload, {});
    }
  }

  updateApplication(applicationId: string, payload: any) {
    const url = `${this.baseUrl}applications/${applicationId}`;
    const headers = new HttpHeaders().set('serviceCode', payload.serviceCode);
    return this.http.put<any>(url, payload, { headers });
  }

  getApplications(
    serviceCode: string,
    filters?: {
      applicationStatus?: string;
      companyCategory?: string;
      registrationYear?: string;
      searchQuery?: string;
    },
    pageNumber: number = 1,
    pageSize: number = 10
  ) {
    const headers = new HttpHeaders().set('serviceCode', serviceCode);

    let params = new HttpParams()
      .set('page', pageNumber.toString())
      .set('size', pageSize.toString());

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params = params.set(key, value);
        }
      });
    }

    const url = `${this.baseUrl}applications/service-code`;
    return this.http.get<any>(url, { headers, params });
  }


  getApplicationById(applicationId: string, serviceCode: string) {
    const url = `${this.baseUrl}applications/service-code/${applicationId}`;
    const headers = new HttpHeaders().set('serviceCode', serviceCode);

    return this.http.get<any>(url, { headers });
  }

  getApplicationForEdit(applicationId: string, serviceCode: string) {
    const url = `${this.baseUrl}applications/edit/${applicationId}`;
    const headers = new HttpHeaders().set('serviceCode', serviceCode);

    return this.http.get<any>(url, { headers });
  }

  verifyKycStatus(applicationId: string): Observable<KycVerificationResponse> {
    const url = `${this.baseUrl}admin/kyc/verify/${applicationId}`;
    return this.http.post<KycVerificationResponse>(url, {});
  }

  resendPendingKycEmails(applicationId: string): Observable<KycResendPendingEmailsResponse> {
    const url = `${this.baseUrl}admin/kyc/resend-pending-emails/${applicationId}`;
    return this.http.post<KycResendPendingEmailsResponse>(url, {});
  }

  getApplicationKycMismatches(applicationId: string): Observable<KycMismatchListResponse> {
    const url = `${this.baseUrl}registry/kyc-mismatches/application/${applicationId}`;
    return this.http.get<KycMismatchListResponse>(url);
  }

  getKycMismatch(mismatchId: string): Observable<KycMismatchResponse> {
    const url = `${this.baseUrl}registry/kyc-mismatches/${mismatchId}`;
    return this.http.get<KycMismatchResponse>(url);
  }

  approveKycMismatch(mismatchId: string, payload: KycMismatchDecisionPayload): Observable<unknown> {
    const url = `${this.baseUrl}registry/kyc-mismatches/${mismatchId}/approve`;
    return this.http.post(url, payload);
  }

  rejectKycMismatch(mismatchId: string, payload: KycMismatchDecisionPayload): Observable<unknown> {
    const url = `${this.baseUrl}registry/kyc-mismatches/${mismatchId}/reject`;
    return this.http.post(url, payload);
  }

  //application approvals
  reviewAction(applicationId: string, event: string, comment?: string, attachmentId?: string) {
    const url = `${this.baseUrl}applications/${applicationId}/review-action`;
    const payload = {
      event: event,
      comment: comment,
      attachmentId: attachmentId
    };
    return this.http.post(url, payload);
  }

  deleteApplication(applicationId?: string, comment?: string) {
    const url = `${this.baseUrl}applications/${applicationId}`;
    const payload = {
      comment: comment,
    };
    return this.http.delete(url, { body: payload });
  }



  //identity verification
  idVerify(identificationDocNumber: string) {
    const params = new HttpParams().set('identificationDocNumber', identificationDocNumber);
    const url = `${this.baseUrl}search/client`;
    return this.http.get<VerifyIdTinResponse>(url, { params });
  }


  companyVerify(value: string) {
    const url = `${this.publicUrl}search/company/minimal`;
    const params = new HttpParams().set('tinNumber', value);
    return this.http.get<VerifyIdTinResponse>(url, { params });
  }

  nameReservationVerify(nameReservationId: string) {
    const url = `${this.publicUrl}search/name-reservation/validate`;
    const params = new HttpParams().set('nameReservationId', nameReservationId);
    return this.http.get<VerifyIdTinResponse>(url, { params });
  }

  searchCompaniesByName(companyName: string): Observable<{ status?: boolean; message?: string; data: PublicCompanySearchItem[] }> {
    const url = `${this.publicUrl}search/companies/search`;
    const params = new HttpParams().set('companyName', companyName);
    return this.http.get<{ status?: boolean; message?: string; data: PublicCompanySearchItem[] }>(url, { params });
  }

  localCompanyVerify(value: string) {
    const url = `${this.baseUrl}company/info`;
    const params = new HttpParams().set('companyNum', value);
    return this.http.get<VerifyIdTinResponse>(url, { params });
  }

  //name reservation
  nameCheck(value: string) {
    const url = `${this.baseUrl}applications/namereservation`;
    const header = new HttpHeaders().set('companyName', value);
    return this.http.get<NameCheckResponse>(url, { headers: header });
  }

  // users
  getAllUsers(
    filters?: {
      searchQuery?: string;
      userStatus?: string;
      roleName?: string;
    },
    pageNumber: number = 1,
    pageSize: number = 10
  ) {
    const url = `${this.baseUrl}users`;
    let params = new HttpParams()
      .set('page', pageNumber.toString())
      .set('size', pageSize.toString());

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params = params.set(key, value);
        }
      });
    }

    return this.http.get<any>(url, { params });
  }

  createUser(payload: any) {
    const url = `${this.baseUrl}users/officers`;
    return this.http.post<any>(url, payload);
  }

  getUser(userId: string) {
    const url = `${this.baseUrl}users/${userId}`;
    return this.http.get<any>(url);
  }

  activateUser(userId: string) {
    const url = `${this.baseUrl}users/${userId}/activate`;
    return this.http.put<any>(url, {});
  }

  deactivateUser(userId: string) {
    const url = `${this.baseUrl}users/${userId}/deactivate`;
    return this.http.put<any>(url, {});
  }

  suspendUser(userId: string) {
    const url = `${this.baseUrl}users/${userId}/suspend`;
    return this.http.put<any>(url, {});
  }

  //companies
  getCompaniesByUser(
    companyStatus: string
  ) {
    const url = `${this.baseUrl}search/companies/my-companies`;
    const params = new HttpParams().set('companyStatus', companyStatus);
    return this.http.get<any>(url, { params });
  }

  getApprovedCompanies(
    pageNumber: number = 1,
    pageSize: number = 10
  ) {
    let params = new HttpParams()
      .set('page', pageNumber.toString())
      .set('size', pageSize.toString());
    const url = `${this.publicUrl}search/companies/approved`;
    return this.http.get<any>(url, { params });
  }

  getCompanyDirectors(companyId: string) {
    const url = `${this.baseUrl}company/directors`;
    const headers = new HttpHeaders().set('companyId', companyId);
    return this.http.get<any>(url, { headers });
  }

  getCompanyShareholders(companyId: string) {
    const url = `${this.baseUrl}company/shareholders`;
    const headers = new HttpHeaders().set('companyId', companyId);
    return this.http.get<any>(url, { headers });
  }

  getCompanyBeneficiaries(companyId: string) {
    const url = `${this.baseUrl}company/beneficiaries`;
    const headers = new HttpHeaders().set('companyId', companyId);
    return this.http.get<any>(url, { headers });
  }

  //dDashboard
  getStatistics() {
    const url = `${this.baseUrl}dashboard/stats`;
    return this.http.get<any>(url);
  }

  // Licensing
  getAllAppointments(status?: string): Observable<AppointmentResponse> {
    const url = `${this.baseUrl}booking`;
    let params = new HttpParams();
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get<AppointmentResponse>(url, { params });
  }


  requestAppointment(appointment: any): Observable<any> {
    const url = `${this.baseUrl}booking`;
    return this.http.post<any>(url, appointment);
  }

  reviewAppointment(id: string, status: string, comment: string) {
    const url = `${this.baseUrl}booking/${id}/status`;
    const payload = {
      status: status,
      comment: comment
    };

    return this.http.put<any>(url, payload);
  }

  licenseVerify(value: string) {
    const url = `${this.publicUrl}search/license/minimal`;
    const params = new HttpParams().set('reference', value);
    return this.http.get<any>(url, { params });
  }
}
